import { RefreshCw, TriangleAlert } from 'lucide-react';

import { Button } from '@repo/ui/components/button';
import { LiquidSurface } from '@repo/ui/components/liquid-surface';

import { useRetryAction } from '@/hooks/use-retry-action';

interface PortfolioRecoveryBannerProps {
  onRetry: () => void;
  isRetrying: boolean;
  /** Which sources failed and with what code — for support and triage. */
  detail?: string;
  /**
   * Plain sentences about what the reader sees because of a failure, for
   * a failure whose effect is not obvious (currency names shown as a
   * shortened address).
   */
  notes?: string[];
}

export function PortfolioRecoveryBanner({
  onRetry,
  isRetrying,
  detail,
  notes = []
}: PortfolioRecoveryBannerProps) {
  const retry = useRetryAction({
    label: 'Retry portfolio data',
    onRetry,
    isRetrying
  });

  return (
    <LiquidSurface
      type="accent"
      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      role="alert"
    >
      <div className="flex min-w-0 items-start gap-3">
        <TriangleAlert
          className="text-rowa-status-warning mt-0.5 size-4 shrink-0"
          aria-hidden
        />
        <div>
          <p className="text-rowa-body-sm text-rowa-text-primary font-medium">
            Some portfolio data could not be refreshed.
          </p>
          <p className="text-rowa-label-sm text-rowa-text-secondary mt-0.5">
            Live figures remain visible. Retry before using this view for a
            decision.
          </p>
          {notes.map((note) => (
            <p
              key={note}
              className="text-rowa-label-sm text-rowa-text-secondary mt-0.5"
            >
              {note}
            </p>
          ))}
          {detail ? (
            <p className="text-rowa-label-sm text-rowa-text-tertiary mt-1 font-mono break-all">
              {detail}
            </p>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={retry.onClick}
        disabled={retry.disabled}
        loading={retry.loading}
        loadingLabel="Retrying"
        className="self-start sm:self-auto"
      >
        <RefreshCw aria-hidden />
        {retry.label}
      </Button>
    </LiquidSurface>
  );
}
